import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import {
  assignExperimentValue,
  getExperiment,
  initializeStatsig,
  trackPerformanceLegacy,
  trackPerformance,
  assignMultiExperimentValue,
} from '@libs/feature-toggle';
import { getLocale } from '@libs/sembo-dates';
import { decodeHTML } from '@libs/utils';

import App from './App.vue';
import { languageLocale } from './modules/localization/localeUtils';
import { createLogger } from './modules/plugins/logger';
import './styles/main.scss';
import { logException } from './modules/plugins/logException';

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

trackPerformance();
trackPerformanceLegacy();

(async function (): Promise<void> {
  await initializeStatsig();

  const shouldShowTripSummaryPreCheckout = getExperiment(
    'trip_summary_pre_checkout_experiment'
  ).get('show_trip_summary_pre_checkout', false);
  assignExperimentValue(
    'trip_summary_pre_checkout_experiment',
    shouldShowTripSummaryPreCheckout
  );

  const overviewShorterHotelsCarousel = getExperiment(
    'overview_shorter_hotels_carousel'
  ).get('use_overview_shorter_hotels_carousel', 'control');
  assignMultiExperimentValue(
    'overview_shorter_hotels_carousel',
    overviewShorterHotelsCarousel
  );

  const overviewPlaceholderNoTransport = getExperiment(
    'overview_placeholder_no_transport_experiment'
  ).get('is_overview_placeholder_no_transport_experiment', false);
  assignExperimentValue(
    'overview_placeholder_no_transport_experiment',
    overviewPlaceholderNoTransport
  );

  const alternativeCopyForMobileFooter = getExperiment(
    'alterantive_copy_for_mobile_footer'
  ).get('should_use_book_copy', false);
  assignExperimentValue(
    'alterantive_copy_for_mobile_footer',
    alternativeCopyForMobileFooter
  );

  const alternativeSuggestedItineraryHotels = getExperiment(
    'alternative_suggested_itinerary_hotels'
  ).get('are_alternative_suggested_itinerary_hotels', false);
  assignExperimentValue(
    'alternative_suggested_itinerary_hotels',
    alternativeSuggestedItineraryHotels
  );

  const sembonusInHotelDetails = getExperiment('sembonus_in_hotel_details').get(
    'should_show_sembonus_in_hotel_details',
    false
  );
  assignExperimentValue('sembonus_in_hotel_details', sembonusInHotelDetails);

  const removeProductLinks = getExperiment('remove_product_links').get(
    'should_hide_links',
    false
  );
  assignExperimentValue('remove_product_links', removeProductLinks);

  const priceCalendarBannerOrderExperiment = getExperiment(
    'price_calendar_banner_order'
  ).get('show_price_calendar_banner_order', false);
  assignExperimentValue(
    'price_calendar_banner_order',
    priceCalendarBannerOrderExperiment
  );

  const productMapContent = getExperiment('product_map_content').get(
    'included_content',
    'NOT_LOADED'
  );
  assignMultiExperimentValue('product_map_content', productMapContent);

  const topAddDestinationOverviewButtonExperiment = getExperiment(
    'top_add_destination_overview_experiment'
  ).get('should_show_top_add_destination_overview_button', false);
  assignExperimentValue(
    'top_add_destination_overview_experiment',
    topAddDestinationOverviewButtonExperiment
  );

  const staticMapboxMap = getExperiment('static_mapbox_map').get(
    'use_mapbox_map',
    false
  );
  assignExperimentValue('static_mapbox_map', staticMapboxMap);
})().catch(() => logException('Statsig experiment initialization failed'));

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

// NOTE: This loads sembo shared web components after app is mounted,
// to not delay first request to smart search.
window.addEventListener('load', () => {
  const scripts = [
    'https://sembo-inspire-apis.sembo.travel/share-component/latest/main.js',
    'https://sembo-inspire-apis.sembo.travel/sembo-auth/latest/sembo-auth.js',
  ];

  scripts.forEach((src) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  });
});

setDefaultOptions({
  locale: getLocale(languageLocale),
});

loadLanguageAsync(languageLocale).catch(() =>
  logException('Internationalization language module loading failed')
);
